import React, { useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import {
    useAddAttachmentsMutation,
    useDeleteAttachmentMutation,
    useGetIndividualReservationQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { handleHttpError } from '../../../../shared/utils';
import FormUpload from '../../../shared/FormUpload';
import toast from '../../../shared/Toast';
import { Media } from '../../../types/user';
import { theme } from '../../../../shared/theme';

const { REACT_APP_BASE_URL } = process.env;

type Attachment = {
    src: string;
    id: string;
    uuid: string;
    name: string;
    createdAt: string;
    addedBy: { firstName: string; lastName: string };
};

type Props = {
    bookingId: number;
};

export default function ReservationAttachmentsCard({ bookingId }: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const fullScreen = useMediaQuery('(max-width: 600px)');

    const { data: reservation } = useGetIndividualReservationQuery(
        `bookings/${bookingId}`,
        { skip: !bookingId },
    );

    const attachments: Attachment[] =
        (reservation?.attachments as Attachment[]) ||
        (reservation?.bookingInfo?.attachments as Attachment[]) ||
        [];

    const totalAttachments = attachments?.length ?? 0;

    const [open, setOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [attachmentUuid, setAttachmentUuid] = useState('');
    const [attachmentName, setAttachmentName] = useState('');

    const [addAttachments] = useAddAttachmentsMutation();
    const [deleteAttachment] = useDeleteAttachmentMutation();

    const { control } = useForm({
        defaultValues: { attachments: [] as any[] },
    });

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setOpen(false);
        setConfirmOpen(false);
        setAttachmentUuid('');
        setAttachmentName('');
    };

    const openConfirmDelete = (uuid: string, name: string) => {
        setAttachmentUuid(uuid);
        setAttachmentName(name);
        setConfirmOpen(true);
    };

    const closeConfirmDelete = () => {
        setConfirmOpen(false);
        setAttachmentUuid('');
        setAttachmentName('');
    };

    const handleAddAttachments = async (
        files: File[],
    ): Promise<Media[] | undefined> => {
        const formData = new FormData();
        for (const file of files) formData.append('attachments', file);

        const requestInfo = [bookingId, formData];

        try {
            const response = await addAttachments(requestInfo).unwrap();
            toast.success(`${files.length} attachment(s) added successfully`);
            return response;
        } catch (error: any) {
            const errorMessage = error?.data?.message || '';

            let message: string;
            if (errorMessage.includes('expected size is less than')) {
                message =
                    'One or more of the attachments exceeds the 10MB size limit.';
            } else if (errorMessage.includes('expected type is')) {
                message =
                    'Invalid format. Expected formats are: image/*|.pdf|.doc|.docx|text/plain|.xlsx|.xls|.csv';
            } else {
                message = 'Failed to add attachments.';
            }

            toast.error(message);
            return undefined;
        }
    };

    const handleDeleteAttachment = () => {
        const requestInfo = [bookingId, attachmentUuid];

        deleteAttachment(requestInfo).then((data: any) => {
            if (!data) {
                toast.error(`Failed to remove attachment`);
                return;
            }

            if (data?.error) {
                toast.error(handleHttpError(data?.error));
                return;
            }

            toast.success(`Attachment removed`);
            closeConfirmDelete();
        });
    };

    const helperText = useMemo(() => {
        if (!totalAttachments) return 'No attachments yet';
        return `${totalAttachments} file${totalAttachments === 1 ? '' : 's'} added`;
    }, [totalAttachments]);

    return (
        <>
            <Box
                onClick={handleOpen}
                role='button'
                tabIndex={0}
                sx={{
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.custom.cardActionBackground,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    '&:hover': {
                        borderColor: theme.palette.info.main,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.neutral.main,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        }}
                    >
                        <AttachFileIcon
                            fontSize='small'
                            sx={{ opacity: 0.75 }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            variant='body3'
                            sx={{
                                fontWeight: 800,
                                letterSpacing: 0.2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexWrap: 'wrap',
                            }}
                        >
                            Attachments ({totalAttachments})
                            <VisibilityOffIcon
                                sx={{ opacity: 0.55 }}
                                fontSize='small'
                            />
                        </Typography>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                                lineHeight: 1.2,
                            }}
                        >
                            {helperText}
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant='body3'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Open
                </Typography>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: fullScreen ? 0 : 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        height: fullScreen ? '100dvh' : 'auto',
                        backgroundColor: theme.palette.neutral.main,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        position: fullScreen ? 'sticky' : 'relative',
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            Attachments{' '}
                            <VisibilityOffIcon
                                sx={{ opacity: 0.55 }}
                                fontSize='small'
                            />
                        </Box>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                            }}
                        >
                            Add files for this reservation.
                        </Typography>
                    </Box>

                    <IconButton onClick={handleClose} size='small'>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        overflow: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            p: 1.75,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 900,
                                fontSize: 12,
                                letterSpacing: 0.2,
                            }}
                        >
                            Upload
                        </Typography>

                        <FormUpload
                            name='attachments'
                            control={control}
                            onUpload={handleAddAttachments}
                            showFiles={false}
                            validation={{
                                acceptedTypes: {
                                    'image/*': [
                                        '.png',
                                        '.jpg',
                                        '.jpeg',
                                        '.gif',
                                        '.webp',
                                    ],
                                    'application/pdf': ['.pdf'],
                                    'application/msword': ['.doc'],
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                        ['.docx'],
                                    'text/plain': ['.txt'],
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                                        ['.xlsx'],
                                    'application/vnd.ms-excel': ['.xls'],
                                    'text/csv': ['.csv'],
                                },
                                maxFileSize: 10 * 1024 * 1024,
                                orientation: 'any',
                            }}
                            uploadText='Click to browse attachments or drag and drop'
                        />
                    </Box>

                    <Box
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            overflow: 'hidden',
                            backgroundColor: theme.palette.neutral.main,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 160,
                            flex: fullScreen ? 1 : 'unset',
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor:
                                    theme.palette.custom.cardActionBackground,
                                borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: 12,
                                    letterSpacing: 0.2,
                                }}
                            >
                                Files
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: 12,
                                }}
                            >
                                {totalAttachments}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                flex: 1,
                                overflow: 'auto',
                                WebkitOverflowScrolling: 'touch',
                            }}
                        >
                            {totalAttachments ? (
                                <Grid container spacing={1.25}>
                                    {attachments.map((a) => (
                                        <Grid item xs={12} key={a.uuid}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'space-between',
                                                    gap: 1,
                                                    py: 0.75,
                                                    px: 1,
                                                    borderRadius: 1.5,
                                                    backgroundColor: alpha(
                                                        theme.palette.yellow
                                                            .main,
                                                        0.18,
                                                    ),
                                                    border: `1px solid ${alpha(theme.palette.yellow.main, 0.45)}`,
                                                    mb: 0.75,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        minWidth: 0,
                                                        flex: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '12px',
                                                            cursor: a?.src
                                                                ? 'pointer'
                                                                : 'default',
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                            whiteSpace:
                                                                'nowrap',
                                                        }}
                                                        onClick={() =>
                                                            window.open(
                                                                a.src,
                                                                '_blank',
                                                            )
                                                        }
                                                        title={a.name}
                                                    >
                                                        {a.name}
                                                    </Typography>

                                                    <Typography
                                                        variant='body3'
                                                        sx={{
                                                            mt: 0.5,
                                                            fontSize: 11,
                                                            color: theme.palette
                                                                .text.secondary,
                                                        }}
                                                    >
                                                        {moment(
                                                            a.createdAt,
                                                        ).format(
                                                            'DD/MM/YYYY, h:mm a',
                                                        )}{' '}
                                                        — {a.addedBy?.firstName}{' '}
                                                        {a.addedBy?.lastName}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <a
                                                        href={`${REACT_APP_BASE_URL}/bookings/${bookingId}/attachments/${a.uuid}`}
                                                    >
                                                        <FileDownloadIcon
                                                            sx={{
                                                                fontSize:
                                                                    'large',
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .primary,
                                                            }}
                                                        />
                                                    </a>

                                                    <CustomIconButton
                                                        onClick={() =>
                                                            openConfirmDelete(
                                                                a.uuid,
                                                                a.name,
                                                            )
                                                        }
                                                        sx={{
                                                            '&:hover': {
                                                                cursor: 'pointer',
                                                                color: 'red',
                                                                backgroundColor:
                                                                    'transparent',
                                                            },
                                                        }}
                                                    >
                                                        <ClearIcon
                                                            sx={{
                                                                fontSize:
                                                                    'large',
                                                            }}
                                                        />
                                                    </CustomIconButton>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontSize: 13,
                                    }}
                                >
                                    No attachments have been added yet.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: 'flex-end',
                        gap: 1,
                        position: fullScreen ? 'sticky' : 'relative',
                        bottom: 0,
                        zIndex: 2,
                    }}
                >
                    <Button
                        variant='outlined'
                        onClick={handleClose}
                        sx={{
                            borderRadius: 2,
                            minHeight: 44,
                            width: fullScreen ? '100%' : 'auto',
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={closeConfirmDelete}
                fullScreen={false}
                fullWidth
                maxWidth='sm'
                sx={{
                    ...(isMobile
                        ? {
                              '& .MuiDialog-container': {
                                  alignItems: 'flex-end',
                              },
                              '& .MuiPaper-root': {
                                  m: 0,
                                  width: '100%',
                                  maxWidth: '100%',
                                  borderRadius: 0,
                                  borderTopLeftRadius: 16,
                                  borderTopRightRadius: 16,
                              },
                          }
                        : {}),
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.palette.neutral.main,
                    },
                }}
            >
                <DialogTitle sx={{ gap: 2 }}>
                    Remove attachment?
                    <IconButton onClick={closeConfirmDelete} size='small'>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                backgroundColor:
                                    theme.palette.custom.cardActionBackground,
                                px: 1.5,
                                py: 1.25,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: theme.palette.text.secondary,
                                    mb: 0.5,
                                }}
                            >
                                Attachment
                            </Typography>

                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: 13,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                title={attachmentName}
                            >
                                {attachmentName}
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            You can’t undo this action.
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ gap: 1 }}>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            gap: 1,
                        }}
                    >
                        <Button
                            variant='outlined'
                            onClick={closeConfirmDelete}
                            sx={{
                                flex: 1,
                                minHeight: 44,
                                borderRadius: 2,
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant='contained'
                            color='warning'
                            onClick={handleDeleteAttachment}
                            sx={{
                                flex: 1,
                                minHeight: 44,
                                borderRadius: 2,
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}
